import styled from 'styled-components';
import * as yup from 'yup';

import Button from 'components/shared/Button';
import CreatableSelect from 'components/shared/form/CreatableSelect';
import FieldLabel from 'components/shared/form/FieldLabel';
import Input from 'components/shared/form/Input';
import StandardModal from 'components/shared/StandardModal';
import { FormProvider, useForm } from 'context/form';
import { FormHelpers, FormMeta, FormValues } from 'context/form/types';
import useInvalidateQueries from 'hooks/useInvalidateQueries';
import useMutateWithToast from 'hooks/useMutateWithToast';
import { HighCostMedicationTier } from 'models/HighCostMedicationList';
import { HighCostMedicationListItem } from 'models/HighCostMedicationListItem';
import {
  createHighCostMedicationListItem,
  HighCostMedicationListItemMutationParams,
  updateHighCostMedicationListItem,
} from 'services/api/highCostMedicationListItems';
import { useToastActions } from 'stores/toastStore';

type HighCostMedEntryFormValues = {
  genericName: string;
  brandName: string;
  // The tier's free-text label, not an id: typing one is how a tier comes into being.
  costTier: string;
  dosage: string;
  route: string;
  form: string;
};

type HighCostMedEntryFormMeta = {
  isSubmitting: boolean;
};

// genericName is the only required field: brandName is a match key olio-ai compares against and the
// rest is reviewer context. Duplicate names are allowed on purpose — the same generic legitimately
// appears under several brands at different tiers. See design 001.
const highCostMedEntrySchema = yup.object({
  genericName: yup.string().trim().required(),
  brandName: yup.string().nullable(),
  costTier: yup.string().nullable(),
  dosage: yup.string().nullable(),
  route: yup.string().nullable(),
  form: yup.string().nullable(),
});

export type HighCostMedEntryModalProps = {
  highCostMedicationListItem?: HighCostMedicationListItem;
  tiers: HighCostMedicationTier[];
  onClose: () => void;
};

// One two-mode modal serving both the header's Add New Medication and the row kebab's Edit details.
// No review step, unlike the denial list's entry modal: Cancel and the submit button act directly.
// See design 003, "Modals".
export default function HighCostMedEntryModal({
  highCostMedicationListItem,
  tiers,
  onClose,
}: HighCostMedEntryModalProps) {
  const isEdit = !!highCostMedicationListItem;

  const invalidateQueries = useInvalidateQueries();
  const { addToast } = useToastActions();

  const { mutate } = useMutateWithToast({
    mutationFn: (params: HighCostMedicationListItemMutationParams) =>
      isEdit
        ? updateHighCostMedicationListItem({ id: highCostMedicationListItem.id, ...params })
        : createHighCostMedicationListItem(params),
  });

  const handleSubmit = (
    values: FormValues<HighCostMedEntryFormValues>,
    _meta: FormMeta<HighCostMedEntryFormMeta>,
    helpers: FormHelpers<HighCostMedEntryFormValues, HighCostMedEntryFormMeta>
  ) => {
    helpers.setMeta('isSubmitting', true);

    const genericName = (values.genericName ?? '').trim();
    // Every optional field goes as null rather than an empty string, so absence is stored as absence.
    const params: HighCostMedicationListItemMutationParams = {
      genericName,
      brandName: values.brandName?.trim() || null,
      costTier: values.costTier?.trim() || null,
      dosage: values.dosage?.trim() || null,
      route: values.route?.trim() || null,
      form: values.form?.trim() || null,
    };

    mutate(params, {
      onSuccess: () => {
        // Tiers too: a typed label may have just created one, and retyping or clearing a label may
        // have retired the last reference to the old one.
        invalidateQueries({ highCostMedicationListItems: true, highCostMedicationTiers: true });
        addToast({
          text: isEdit
            ? `${genericName} successfully updated`
            : `${genericName} successfully added to the high cost med list`,
        });
        onClose();
      },
      onSettled: () => {
        helpers.setMeta('isSubmitting', false);
      },
    });
  };

  return (
    <StandardModal title={isEdit ? 'Edit High Cost Medication' : 'Add New High Cost Medication'} onCancel={onClose}>
      <FormProvider<HighCostMedEntryFormValues, HighCostMedEntryFormMeta>
        onSubmit={handleSubmit}
        yupSchema={highCostMedEntrySchema}
        initialValues={{
          genericName: highCostMedicationListItem?.genericName ?? '',
          brandName: highCostMedicationListItem?.brandName ?? '',
          costTier: highCostMedicationListItem?.tier?.label ?? '',
          dosage: highCostMedicationListItem?.dosage ?? '',
          route: highCostMedicationListItem?.route ?? '',
          form: highCostMedicationListItem?.form ?? '',
        }}
        initialMeta={{ isSubmitting: false }}>
        <HighCostMedEntryFields isEdit={isEdit} tiers={tiers} onClose={onClose} />
      </FormProvider>
    </StandardModal>
  );
}

function HighCostMedEntryFields({
  isEdit,
  tiers,
  onClose,
}: {
  isEdit: boolean;
  tiers: HighCostMedicationTier[];
  onClose: () => void;
}) {
  const { values, setValue, valid, dirty, meta, submitForm } = useForm<
    HighCostMedEntryFormValues,
    HighCostMedEntryFormMeta
  >();

  const isSubmitting = !!meta.isSubmitting;

  return (
    <>
      <Fields>
        <Field>
          <FieldLabel>Medication Name</FieldLabel>
          <Input
            value={values.genericName ?? ''}
            onChange={(e) => setValue('genericName', e.target.value)}
            placeholder='Medication Name'
            disabled={isSubmitting}
          />
        </Field>

        <Field>
          <FieldLabel>Brand Name (optional)</FieldLabel>
          <Input
            value={values.brandName ?? ''}
            onChange={(e) => setValue('brandName', e.target.value)}
            placeholder='Brand Name'
            disabled={isSubmitting}
          />
        </Field>

        {/* The organization's existing labels are offered as a convenience, but anything can be
            typed — that is how a tier comes into being, and an organization whose key is still empty
            has to be able to name its first one. Writing a tier's description stays admin-only. */}
        <Field>
          <FieldLabel>Cost Tier (optional)</FieldLabel>
          <CreatableSelect
            options={tiers.map((tier) => tier.label)}
            value={values.costTier ?? ''}
            onChange={(costTier) => setValue('costTier', costTier)}
            placeholder='Select a cost tier'
            disabled={isSubmitting}
            dataTestId='cost-tier-select'
          />
        </Field>

        <Field>
          <FieldLabel>Dosage (optional)</FieldLabel>
          <Input
            value={values.dosage ?? ''}
            onChange={(e) => setValue('dosage', e.target.value)}
            placeholder='Dosage'
            disabled={isSubmitting}
          />
        </Field>

        <Field>
          <FieldLabel>Route (optional)</FieldLabel>
          <Input
            value={values.route ?? ''}
            onChange={(e) => setValue('route', e.target.value)}
            placeholder='Route'
            disabled={isSubmitting}
          />
        </Field>

        <Field>
          <FieldLabel>Form (optional)</FieldLabel>
          <Input
            value={values.form ?? ''}
            onChange={(e) => setValue('form', e.target.value)}
            placeholder='Form'
            disabled={isSubmitting}
          />
        </Field>
      </Fields>

      <StandardModal.Actions>
        <Button variant='ghost' onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          variant='primary'
          onClick={() => submitForm()}
          loading={isSubmitting}
          disabled={!valid || !dirty || isSubmitting}>
          {isEdit ? 'Update' : 'Submit'}
        </Button>
      </StandardModal.Actions>
    </>
  );
}

const Fields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 40px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
`;
